/*
 * Whose faces the Agents button and the Agents menu header show, from one
 * helper so the two never differ: the agents waiting on you (most urgent
 * first), else the agents running now, else the agents you worked with most
 * recently, topped up for new users from a fixed fallback set.
 */
import type { RunningTurn } from "@/features/agents/runningTurnsStore";
import { moodPresetFor } from "@/features/agents/components/AgentMoodAvatar";

import type { AttentionItem } from "./types";

/**
 * Three seeds on three different mood presets. The idle stack is topped up
 * from these while someone has fewer than two agents of their own.
 */
export const FALLBACK_AGENT_SEEDS: readonly string[] = [
  "agent:gus",
  "agent:dot",
  "agent:ivy"
];

export const itemSeed = (
  item: Pick<AttentionItem, "scope_kind" | "scope_id">
): string => `${item.scope_kind}:${item.scope_id}`;

export const turnSeed = (turn: Pick<RunningTurn, "scope">): string =>
  `${turn.scope.kind}:${turn.scope.id}`;

const unique = (seeds: string[]): string[] => Array.from(new Set(seeds));

export type StackSource = {
  /** Most urgent first. */
  waiting: AttentionItem[];
  running: RunningTurn[];
  /** Recent non-practice channel seeds, newest first. */
  recent: string[];
  /** The Practice Assistant's seed: its look stays with the tab bar's agent button. */
  practiceSeed?: string | null;
};

export function agentStackSeeds({
  waiting,
  running,
  recent,
  practiceSeed
}: StackSource): string[] {
  if (waiting.length) return unique(waiting.map(itemSeed));
  if (running.length) return unique(running.map(turnSeed));
  const own = unique(recent.filter((s) => !s.startsWith("practice:")));
  if (own.length >= 2) return own;
  const taken = new Set(own.map((s) => JSON.stringify(moodPresetFor(s))));
  if (practiceSeed) taken.add(JSON.stringify(moodPresetFor(practiceSeed)));
  const filler = FALLBACK_AGENT_SEEDS.filter((s) => {
    const look = JSON.stringify(moodPresetFor(s));
    if (taken.has(look)) return false;
    taken.add(look);
    return true;
  });
  return [...own, ...filler];
}
